// pricing-calculator/hooks/useCalculatorState.ts
// Central calculator state (useReducer-based) + actions.

import { useMemo, useReducer } from 'react';
import type {
  ID,
  CalculatorSpec,
  CalculatorState,
  CalculatorActions,
  ROIInputs,
  ShareableCalculatorState,
} from '@dopomogai/types';
import {
  buildFeatureIndex,
  addWithDependencies,
  removeFeature as removeFeatureWithDependents,
} from '../utils/compat';
import {
  applyPresetToSelection,
  filterSelectionForPlatform,
  toggleInSet,
} from '../utils/selection';

type Action =
  | { type: 'SET_PLATFORM'; platformId: ID }
  | { type: 'SET_PRESET'; presetId?: ID }
  | { type: 'TOGGLE_MODULE'; moduleId: ID }
  | { type: 'ADD_WITH_DEPENDENCIES'; moduleId: ID }
  | { type: 'REMOVE_WITH_DEPENDENTS'; moduleId: ID }
  | { type: 'SET_ROI_INPUTS'; patch: Partial<ROIInputs> }
  | { type: 'SET_SEGMENT'; segmentKey?: string } // NEW
  | { type: 'CLEAR' };

function resolveInitialPlatformId(spec: CalculatorSpec, prefill?: ShareableCalculatorState): ID {
  return prefill?.platformId || spec.platforms[0]?.id || '';
}

function resolveInitialPresetId(spec: CalculatorSpec, prefill?: ShareableCalculatorState): ID | undefined {
  return prefill?.presetId || spec.presets?.find((p) => p.recommended)?.id;
}

function resolveInitialSelected(spec: CalculatorSpec, prefill?: ShareableCalculatorState, presetId?: ID): Set<ID> {
  // If a preset is present, start from preset strictly
  if (presetId) {
    const p = spec.presets?.find((x) => x.id === presetId);
    if (p) return new Set(p.moduleIds);
  }
  const list = prefill?.selectedModuleIds || [];
  return new Set(list);
}

function resolveInitialROI(spec: CalculatorSpec, prefill?: ShareableCalculatorState): ROIInputs {
  return {
    sessions: prefill?.roi?.sessions ?? spec.roiModelConfig?.defaultInputs?.sessions,
    cvrPct: prefill?.roi?.cvrPct ?? spec.roiModelConfig?.defaultInputs?.cvrPct,
    aov: prefill?.roi?.aov ?? spec.roiModelConfig?.defaultInputs?.aov,
    marginPct: prefill?.roi?.marginPct ?? spec.roiModelConfig?.defaultInputs?.marginPct,
    ticketsMonthly: prefill?.roi?.ticketsMonthly ?? spec.roiModelConfig?.defaultInputs?.ticketsMonthly,
    costPerTicket: prefill?.roi?.costPerTicket ?? spec.roiModelConfig?.defaultInputs?.costPerTicket,
  };
}

function createInitialState(spec: CalculatorSpec, prefill?: ShareableCalculatorState): CalculatorState {
  const platformId = resolveInitialPlatformId(spec, prefill);
  const presetId = resolveInitialPresetId(spec, prefill);
  let selected = resolveInitialSelected(spec, prefill, presetId);

  const platform = spec.platforms.find((p) => p.id === platformId);
  selected = filterSelectionForPlatform(selected, spec.groups, platform);

  // NEW: segmentKey in state; keep undefined initially
  return {
    platformId,
    presetId,
    selectedModuleIds: selected,
    roiInputs: resolveInitialROI(spec, prefill),
    segmentKey: undefined,
  } as CalculatorState;
}

function reducer(spec: CalculatorSpec, featureIndex: ReturnType<typeof buildFeatureIndex>) {
  return (state: CalculatorState, action: Action): CalculatorState => {
    switch (action.type) {
      case 'SET_PLATFORM': {
        const platform = spec.platforms.find((p) => p.id === action.platformId) || spec.platforms[0];
        // Prune incompatible features
        const nextSelected = filterSelectionForPlatform(state.selectedModuleIds, spec.groups, platform);
        return {
          ...state,
          platformId: platform.id,
          selectedModuleIds: nextSelected,
        } as CalculatorState;
      }

      case 'SET_PRESET': {
        const preset = spec.presets?.find((p) => p.id === action.presetId);
        let nextSelected = applyPresetToSelection(state.selectedModuleIds, preset, false);
        // Also prune against current platform, if any
        const platform = spec.platforms.find((p) => p.id === state.platformId);
        nextSelected = filterSelectionForPlatform(nextSelected, spec.groups, platform);
        return {
          ...state,
          presetId: action.presetId,
          selectedModuleIds: nextSelected,
        } as CalculatorState;
      }

      case 'TOGGLE_MODULE': {
        const next = toggleInSet(state.selectedModuleIds, action.moduleId);
        return {
          ...state,
          selectedModuleIds: next,
          presetId: undefined, // manual changes clear preset lock
        } as CalculatorState;
      }

      case 'ADD_WITH_DEPENDENCIES': {
        const target = featureIndex[action.moduleId];
        if (!target) return state;
        const { nextSelected } = addWithDependencies(state.selectedModuleIds, target, featureIndex);
        return {
          ...state,
          selectedModuleIds: nextSelected,
          presetId: undefined,
        } as CalculatorState;
      }

      case 'REMOVE_WITH_DEPENDENTS': {
        const { nextSelected } = removeFeatureWithDependents(
          state.selectedModuleIds,
          action.moduleId,
          featureIndex,
          true
        );
        return {
          ...state,
          selectedModuleIds: nextSelected,
          presetId: undefined,
        } as CalculatorState;
      }

      case 'SET_ROI_INPUTS': {
        return { ...state, roiInputs: { ...state.roiInputs, ...action.patch } } as CalculatorState;
      }

      case 'SET_SEGMENT': {
        return { ...state, segmentKey: action.segmentKey } as CalculatorState;
      }

      case 'CLEAR': {
        // Keep platform; clear features/preset; reset ROI to spec defaults
        return {
          platformId: state.platformId,
          selectedModuleIds: new Set<ID>(),
          presetId: undefined,
          roiInputs: resolveInitialROI(spec, undefined),
        } as CalculatorState;
      }

      default:
        return state;
    }
  };
}

/**
 * useCalculatorState
 * Initializes and manages calculator state. Pure domain-level; no UI or side-effects.
 */
export function useCalculatorState(
  spec: CalculatorSpec,
  options?: { prefill?: ShareableCalculatorState }
): { state: CalculatorState; actions: CalculatorActions } {
  const index = useMemo(() => buildFeatureIndex(spec.groups), [spec.groups]);
  const [state, dispatch] = useReducer(reducer(spec, index), createInitialState(spec, options?.prefill));

  const actions: CalculatorActions = useMemo(
    () => ({
      setPlatform: (platformId: ID) => dispatch({ type: 'SET_PLATFORM', platformId }),
      setPreset: (presetId?: ID) => dispatch({ type: 'SET_PRESET', presetId }),
      toggleModule: (moduleId: ID) => dispatch({ type: 'TOGGLE_MODULE', moduleId }),
      setRoiInputs: (patch: Partial<ROIInputs>) => dispatch({ type: 'SET_ROI_INPUTS', patch }),
      clear: () => dispatch({ type: 'CLEAR' }),
      // extra
      setSegment: (segmentKey?: 'smb' | 'mid' | 'enterprise') => dispatch({ type: 'SET_SEGMENT', segmentKey }),
      addWithDependencies: (moduleId: ID) => dispatch({ type: 'ADD_WITH_DEPENDENCIES', moduleId }),
      removeWithDependents: (moduleId: ID) => dispatch({ type: 'REMOVE_WITH_DEPENDENTS', moduleId }),
    }),
    []
  );

  return { state, actions };
}
